'use client';
import { motion } from 'framer-motion';
import React from 'react';
import Container from '../ui/Container';
import HeroSectionHeading from '../ui/HeroSectionHeading';
import SectionHeader from '../ui/SectionHeader';
import Image from 'next/image';
import { IProjectDetail } from '../type/projectDetail/projectDetailType';
import ExploreProject from '../common/ExploreProject';

type ProjectDetailsProps = {
  data: IProjectDetail;
};
const ProjectDetails = ({ data }: ProjectDetailsProps) => {
  return (
    <>
      <div
        className="relative -mt-[74px] mb-[60px] bg-cover bg-center bg-no-repeat 3xl:mb-[100px]"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0) -2.73%, rgba(255, 255, 255) 61.57%),url(${data?.heroSection?.heroBgimg})`,
        }}
      >
        <div className="mx-auto flex w-full max-w-full flex-col justify-between gap-12 overflow-hidden pl-4 pt-[150px] sm:pl-6 md:pl-10 lg:h-screen lg:flex-row xl:pl-20 3xl:pl-[100px]">
          <div className="flex w-full max-w-[785px] flex-col justify-center gap-4 lg:ml-auto lg:pb-[100px]">
            {data?.heroSection?.projectLogo && (
              <Image
                src={data?.heroSection?.projectLogo || ''}
                alt={data?.heroSection?.projectLogo ? 'Project Logo' : ''}
                width={120}
                height={60}
                className="object-contain"
              />
            )}
            <HeroSectionHeading
              description={data?.heroSection?.description || ''}
              className="w-full"
              headingStyle="hidden"
            />
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.8,
              ease: 'easeIn',
            }}
            className="relative ml-auto mt-auto aspect-[5/4] h-full w-full max-w-[450px] lg:max-w-max 3xl:aspect-[12/9]"
          >
            <Image
              src={data?.heroSection?.heroProjectMockupImg}
              alt="moc-img"
              fill
              priority
              fetchPriority="high"
            />
          </motion.div>
        </div>
      </div>
      <div className="bg-workDetailRight bg-contain bg-left bg-no-repeat">
        <Container className="mb-[60px] flex flex-col gap-10 4xl:mb-[100px] 4xl:gap-[60px]">
          <SectionHeader
            className="mx-auto"
            headingStyle="text-center"
            headingText={data?.aboutTheProject?.title}
            descriptionStyle="3xl:max-w-[1000px]"
            descriptionText={data?.aboutTheProject?.description || ''}
          />
          {data?.aboutTheProject?.projectMockupImages && (
            <div className="relative h-0 w-full pb-[45%]">
              <Image
                src={data?.aboutTheProject?.projectMockupImages || ''}
                alt={
                  data?.aboutTheProject?.projectMockupImages
                    ? 'Project Mockup'
                    : ''
                }
                fill
                priority
                fetchPriority={'high'}
                className="h-full w-full"
              />
            </div>
          )}
        </Container>
      </div>
      {data?.projectHomePageImage && (
        <div className="bg-workDetailLeft bg-contain bg-right bg-no-repeat">
          <Container className="mb-[60px] flex !max-w-[1300px] flex-col gap-10 4xl:mb-[100px] 4xl:gap-[60px]">
            <div className="relative w-full">
              <Image
                src={data?.projectHomePageImage || ''}
                alt={data?.projectHomePageImage ? 'Project Home Page' : ''}
                width={1200}
                height={1000}
                priority
                fetchPriority={'high'}
                className="h-auto w-full"
              />
            </div>
          </Container>
        </div>
      )}
      {data?.projectSubMockupImages.length > 0 && (
        <Container className="mb-[60px] flex flex-col gap-10 4xl:mb-[100px] 4xl:gap-[60px]">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {data?.projectSubMockupImages.map((imgSrc, index) => (
              <div key={index} className="relative h-0 w-full pb-[70%]">
                <Image
                  src={imgSrc || ''}
                  alt={imgSrc ? 'Project Sub Mockup' : ''}
                  fill
                  priority
                  fetchPriority={'high'}
                  className="h-full w-full"
                />
              </div>
            ))}
          </div>
        </Container>
      )}
      <Container className="mb-[60px] flex flex-col gap-10 4xl:mb-[100px] 4xl:gap-[60px]">
        <SectionHeader
          headingText={data?.projectSolution?.title}
          className="items-center"
          descriptionText={data?.projectSolution?.description || ''}
        />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {data?.projectSolution?.projectSolutionCard.map((card, index) => (
            <div
              key={index}
              className="flex flex-col gap-5 rounded-xl border border-lightBlueish bg-lightBlueishGray p-6 md:p-8"
            >
              <h3 className="font-opt text-3xl font-medium leading-8 text-primary">
                {card.solutionTitle}
              </h3>
              <p className="font-opt text-base text-optDesc md:text-lg">
                {card.solutionDescription}
              </p>
            </div>
          ))}
        </div>
      </Container>
      <Container className="mb-[60px] 4xl:mb-[100px]">
        <div className="relative z-[-1] grid overflow-hidden grid-stack after:absolute after:inset-0 after:z-[1] after:rounded-xl after:bg-black-200-alpha lg:after:rounded-[32px]">
          <div className="relative h-0 w-full rounded-xl pb-[100%] lg:rounded-[20px] lg:pb-[60%]">
            <Image
              src={data?.projectSolution?.projectUser || ''}
              alt={data?.projectSolution?.projectUser ? 'Project User' : ''}
              fill
              priority
              fetchPriority={'high'}
              className="h-full w-full rounded-xl object-cover lg:rounded-[20px]"
            />
          </div>
          <div className="relative z-10 m-2 flex w-full flex-col justify-between gap-10 rounded-xl bg-black-450-alpha p-3 sm:m-3 md:m-8 md:max-w-[350px]">
            <Image
              src={data?.projectSolution?.projectLogo || ''}
              alt={data?.projectSolution?.projectLogo ? 'Project Logo' : ''}
              width={100}
              height={60}
              className="object-contain"
            />
            <p className="flex-col font-opt text-xl font-medium leading-6 text-white">
              {data?.projectSolution?.subDescription}
            </p>
          </div>
        </div>
      </Container>
      <ExploreProject numberOfCards={3} isInternal={true} />
    </>
  );
};

export default ProjectDetails;
